import React, { useEffect, useMemo, useState } from 'react';
import { Check, Minus, Plus, Sparkles } from 'lucide-react';
import Header from '../components/landing/Header';
import Footer from '../components/landing/Footer';
import { setCanonical } from '../lib/seo';

const SERIF = "'Cormorant Garamond', serif";
const Eyebrow = ({ children, className = '' }) => (
  <p className={`text-[#7A5A2E] text-[11px] tracking-[0.24em] uppercase ${className}`}>{children}</p>
);

const ESSENTIALS_FEATURES = [
  'Unlimited projects',
  'Full project memory, built automatically',
  'AI organization of decisions, approvals, and files',
  'Includes standard AI usage',
  'Client portal & approvals',
  'Vendor communications',
  'Risk alerts',
  'Site visits & meetings',
  'Voice notes',
  'Mobile access',
];

const FREE_FEATURES = [
  '1 active project',
  'Project memory, built automatically',
  'Client approvals',
  'Mobile access',
];

const SeatStepper = ({ seats, setSeats }) => (
  <div className="flex items-center gap-3" data-testid="pricing-seat-stepper">
    <button
      type="button"
      onClick={() => setSeats(Math.max(1, seats - 1))}
      data-testid="pricing-seat-decrement"
      aria-label="Decrease seats"
      className="w-10 h-10 rounded-full border border-white/30 flex items-center justify-center text-white hover:bg-white/10 hover:border-white/60 transition-colors"
    >
      <Minus size={15} />
    </button>
    <span className="text-white text-[17px] font-semibold w-20 text-center" data-testid="pricing-seat-count">
      {seats} {seats === 1 ? 'seat' : 'seats'}
    </span>
    <button
      type="button"
      onClick={() => setSeats(Math.min(200, seats + 1))}
      data-testid="pricing-seat-increment"
      aria-label="Increase seats"
      className="w-10 h-10 rounded-full border border-white/30 flex items-center justify-center text-white hover:bg-white/10 hover:border-white/60 transition-colors"
    >
      <Plus size={15} />
    </button>
  </div>
);

const PlanCard = ({ badge, title, priceLine, priceSub, features, highlight, children, testId }) => (
  <div
    data-testid={testId}
    className={`relative flex flex-col rounded-[24px] p-8 md:p-9 border transition-all duration-300 ${
      highlight
        ? 'bg-[#2A2018] text-[#F5EDE1] border-[#2A2018] shadow-[0_30px_70px_-30px_rgba(42,32,24,0.5)] md:-translate-y-3'
        : 'bg-[#FBF6EF] text-[#1F1812] border-[#2A2018]/[0.08]'
    }`}
  >
    {badge && (
      <span className="absolute -top-3 left-8 bg-[#B8864B] text-white text-[10.5px] font-semibold tracking-[0.14em] uppercase px-3 py-1 rounded-full">
        {badge}
      </span>
    )}
    <h3 className={highlight ? 'text-white' : 'text-[#1F1812]'} style={{ fontFamily: SERIF, fontWeight: 500, fontSize: '24px' }}>
      {title}
    </h3>
    <div className="mt-4 flex items-baseline gap-1.5">
      <span style={{ fontFamily: SERIF, fontWeight: 600, fontSize: 'clamp(2.1rem, 3vw, 2.6rem)' }} className={highlight ? 'text-white' : 'text-[#1F1812]'}>
        {priceLine}
      </span>
    </div>
    {priceSub && <p className={`mt-1.5 text-[13.5px] ${highlight ? 'text-[#D6C6AE]' : 'text-[#7A5A2E]'}`}>{priceSub}</p>}
    {children}
    <ul className="mt-7 space-y-3 flex-1">
      {features.map((f) => (
        <li key={f} className="flex items-start gap-2.5 text-[14.5px] leading-[1.5]">
          <Check size={15} className={`mt-0.5 flex-shrink-0 ${highlight ? 'text-[#C9A567]' : 'text-[#B8864B]'}`} />
          <span className={highlight ? 'text-[#E8D9C2]' : 'text-[#3D3229]'}>{f}</span>
        </li>
      ))}
    </ul>
  </div>
);

const Pricing = () => {
  const [seats, setSeats] = useState(3);
  const [aiCredits, setAiCredits] = useState(false);

  useEffect(() => {
    document.title = 'Pricing | Archen™';
    setCanonical('/pricing');
  }, []);

  const essentialsTotal = seats * 39;
  const aiCreditsTotal = aiCredits ? seats * 10 : 0;
  const grandTotal = essentialsTotal + aiCreditsTotal;

  const totalLabel = useMemo(() => {
    const parts = [`${seats} ${seats === 1 ? 'seat' : 'seats'} x $39`];
    if (aiCredits) parts.push(`+ AI Credits ${seats} x $10`);
    return parts.join('  ');
  }, [seats, aiCredits]);

  return (
    <div className="bg-[#FDF8F3] min-h-screen antialiased pb-20" data-testid="pricing-page" style={{ fontFamily: "'Inter', system-ui, sans-serif" }}>
      <Header />
      <main className="pt-32 md:pt-40 pb-40">
        <div className="content-container text-center">
          <Eyebrow className="mb-6">Pricing</Eyebrow>
          <h1
            className="mx-auto text-[#1F1812] leading-[1.05] tracking-[-0.02em]"
            style={{ fontFamily: SERIF, fontWeight: 500, fontSize: 'clamp(2.6rem, 5vw, 4.2rem)' }}
            data-testid="pricing-hero-headline"
          >
            Simple pricing.<br />No modules to buy back.
          </h1>
          <p className="mt-6 mx-auto text-[#3D3A36] text-[17px] md:text-[19px] leading-[1.6] max-w-[56ch]">
            One free on-ramp, one full plan, and an optional add-on for teams that want more AI usage. That is the whole structure.
          </p>
        </div>

        <div className="content-container mt-16 md:mt-20">
          <div className="grid md:grid-cols-3 gap-6 md:gap-7 items-start">
            <PlanCard
              testId="pricing-card-free"
              title="Free"
              priceLine="$0"
              priceSub="per month"
              features={FREE_FEATURES}
            />
            <PlanCard
              testId="pricing-card-essentials"
              badge="Most Popular"
              highlight
              title="Essentials"
              priceLine="$39"
              priceSub="per seat, per month"
              features={ESSENTIALS_FEATURES}
            >
              <div className="mt-6 p-4 rounded-xl bg-white/[0.07] border border-white/10">
                <p className="text-[#C9A567] text-[11px] tracking-[0.16em] uppercase mb-3">Seats</p>
                <SeatStepper seats={seats} setSeats={setSeats} />
                <p className="mt-3 text-[#F0E4D2] text-[13.5px] font-medium">{seats} x $39 = ${essentialsTotal}/mo</p>
              </div>
            </PlanCard>
            <PlanCard
              testId="pricing-card-ai-credits"
              title="AI Credits"
              priceLine="+$10"
              priceSub="per seat, per month, for usage beyond what's included in Essentials"
              features={['For studios that need more AI usage than the amount included in Essentials', 'Applies across all Essentials seats', 'Turn on or off any time']}
            >
              <div className="mt-6 pt-5 border-t border-[#2A2018]/10">
                <button
                  type="button"
                  onClick={() => setAiCredits((v) => !v)}
                  data-testid="pricing-ai-credits-toggle"
                  aria-pressed={aiCredits}
                  className={`w-full flex items-center justify-between gap-3 rounded-lg px-4 py-3 text-[13.5px] font-medium transition-colors ${
                    aiCredits ? 'bg-[#EDE3D5] text-[#1F1812]' : 'bg-white border border-[#2A2018]/10 text-[#5C4F42]'
                  }`}
                >
                  <span className="flex items-center gap-2">
                    <Sparkles size={14} className="text-[#B8864B]" />
                    {aiCredits ? 'AI Credits enabled' : 'Enable AI Credits'}
                  </span>
                  <span className={`w-9 h-5 rounded-full relative transition-colors ${aiCredits ? 'bg-[#B8864B]' : 'bg-[#E2D9CB]'}`}>
                    <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white transition-transform ${aiCredits ? 'translate-x-4' : ''}`} />
                  </span>
                </button>
              </div>
            </PlanCard>
          </div>

          <p className="mt-10 text-center text-[#6B655C] text-[13px]" data-testid="pricing-founding-note">
            Archen™ pricing shown here is for reference. Self-serve billing is not yet available; this page is informational only.
          </p>
        </div>
      </main>
      <Footer />

      {/* Sticky summary footer (informational total only, no CTA) */}
      <div
        className="fixed bottom-0 left-0 right-0 z-40 bg-[#1F1812] border-t border-white/10"
        data-testid="pricing-sticky-footer"
      >
        <div className="content-container py-4 flex items-center justify-center">
          <p className="text-[#E8D9C2] text-[13.5px]" data-testid="pricing-sticky-summary">
            {totalLabel} <span className="text-white font-semibold">= ${grandTotal}/mo total</span>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Pricing;
